import { useLayoutEffect, useState, RefObject } from 'react';

/**
 * Custom hook that checks if text overflows its container using Canvas API for accurate measurement.
 * Handles dynamic container resizing with ResizeObserver.
 *
 * @param textRef - Ref to the text element whose styles will be used for measurement
 * @param containerRef - Ref to the container element that constrains the text width
 * @param text - The text string to measure
 * @param reservedWidth - Optional reserved width (e.g., for buttons) to subtract from available space. Defaults to 0.
 * @returns boolean indicating whether the text overflows the available space
 */
export function useTextOverflow(
  textRef: RefObject<HTMLElement>,
  containerRef: RefObject<HTMLElement>,
  text: string,
  reservedWidth: number = 0
): boolean {
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);

  useLayoutEffect(() => {
    const textElement = textRef.current;
    const containerElement = containerRef.current;

    if (!textElement || !containerElement) {
      return;
    }

    const checkOverflow = (): void => {
      if (
        !textElement ||
        !containerElement ||
        containerElement.clientWidth === 0
      ) {
        return;
      }

      // Use Canvas API to measure text width accurately
      // scrollWidth doesn't work correctly with text-overflow: ellipsis
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        return;
      }

      const computedStyle = window.getComputedStyle(textElement);
      // Set font properties to match the text element
      context.font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

      const textWidth = context.measureText(text).width;

      // Check if text overflows - compare measured width with available space
      const availableWidth = containerElement.clientWidth - reservedWidth;
      setHasOverflow(textWidth > availableWidth);
    };

    // Initial check
    checkOverflow();

    // ResizeObserver detects container size changes (including column resize)
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(containerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [textRef, containerRef, text, reservedWidth]);

  return hasOverflow;
}
